import { ConfirmationDialog, MessagePanel } from '@dittolive/anvil'
import graphql from 'babel-plugin-relay/macro'
import TextBoxConfirmation from 'components/confirmation/TextBoxConfirmation'
import { useState } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { toast } from 'sonner'
import { usePortalMutation } from 'utils'

import { DeleteAppModalMutation } from './__generated__/DeleteAppModalMutation.graphql'
import { DeleteAppModalQuery } from './__generated__/DeleteAppModalQuery.graphql'

const query = graphql`
  query DeleteAppModalQuery($id: ID!) {
    viewer {
      appById(id: $id) {
        slug
      }
    }
  }
`

const deleteMutation = graphql`
  mutation DeleteAppModalMutation($id: ID!) {
    deleteApp(id: $id)
  }
`

type Props = {
  /** True if the modal is open. */
  isOpen: boolean
  /** Callback for closing the modal */
  onClose: (redirect: boolean) => void
  /** ID of the app being deleted. */
  appId: string
}

/** Modal used to confirm that an app should be deleted. */
const DeleteAppModal = ({ isOpen, onClose, appId }: Props) => {
  const { viewer } = useLazyLoadQuery<DeleteAppModalQuery>(query, { id: appId })
  const [deleteApp, isDeletingApp] = usePortalMutation<DeleteAppModalMutation>(
    deleteMutation,
    {
      mutationName: 'DeleteApp',
      sentryContext: {
        component: 'DeleteAppModal',
        appId,
      },
      showErrorToast: false,
    },
  )
  const [isMatch, setIsMatch] = useState(false)
  const appSlug = viewer.appById?.slug

  const submit = () => {
    deleteApp({
      variables: {
        id: appId,
      },
      onCompleted: () => onClose(true),
      onError: () => {
        toast.error('We ran into an issue deleting the database')
      },
    })
  }

  return (
    <ConfirmationDialog
      open={isOpen}
      onCancel={() => onClose(false)}
      onConfirm={submit}
      title="Delete database"
      confirmButtonText="Confirm"
      cancelButtonDisabled={isDeletingApp}
      confirmButtonDisabled={!isMatch || isDeletingApp}
      variant="warning"
      description="Are you sure you want to delete your database?"
      data-testid="modalDialogPanel"
    >
      <MessagePanel
        variant="danger"
        message="Once you confirm this action there's no going back. Be certain that you want to delete this database and all data related to it."
        className="mb-8"
      />
      <TextBoxConfirmation onMatch={setIsMatch} value={appSlug || ''} />
    </ConfirmationDialog>
  )
}

export default DeleteAppModal
